/**
 * blockChildThicknessSweep — a block-child heavy `POLYLINE`'s group-39
 * THICKNESS under the INSERT affine (@codex round 17, FINDING A).
 *
 * # The defect
 *
 * Group 39 is GEOMETRY, not a property: Autodesk's DXF reference ("Thickness
 * (39)" + "Object Coordinate Systems (OCS)") defines it as the EXTRUSION of the
 * entity along its extrusion direction, and since round 15
 * `../geom/polylineThicknessSweep.ts` paints that ruled surface — the drawn
 * outline, its copy translated by `t·N̂`, and the rulings between them.
 *
 * `transformBlockChild`'s `Polyline` arm transforms the child's vertices and its
 * PLANE, but a group 39 rode the `...child` spread UNCHANGED, and
 * `polylineFromEntity` then re-resolved `t·N̂` from that authored scalar. So a
 * thickness-5 child inside a uniform ×2 INSERT painted a 5-unit sweep while
 * every vertex of the same child had already doubled — measured, before this
 * module: child `(0,0) → (10,0)`, `thickness: 5`, INSERT `scale (2,2,2)` drew
 * `points (0,0) → (20,0)` with `thicknessSweep (0,0,5)`, where the affine image
 * of the sweep vector is `(0,0,10)`.
 *
 * # Why a SCALAR on the clone cannot close it
 *
 * The swept surface of the child is `{p + s·t·N̂}`, so its image under the
 * insert affine `A` is `{A(p) + s·M·(t·N̂)}` — the ruling direction is
 * `M·(t·N̂)`, the linear part applied to the sweep VECTOR. The clone can only
 * say "scalar × my group 210", and its group 210 is the image PLANE's normal,
 * which `ocsPlanarTransform` derives as `normalize(M·ax × M·ay)` — the
 * inverse-transpose direction. Those two agree only when `M` is a 3D
 * similarity. Under anything else the image of the sweep is NOT perpendicular
 * to the image of the plane, so the magnitude AND the direction have to travel,
 * and no group-39 / group-210 pair on the clone can spell that.
 *
 * `SceneGeometry.polyline.thicknessSweep` ALREADY carries a resolved WORLD
 * vector for exactly that reason, so the answer is stamped there rather than
 * pushed through a second scalar — the same division of labour
 * `./blockChildBandFrame.ts` makes for the width band, whose width is likewise
 * not a scalar under a non-similarity map.
 *
 * # ONE reading of "which way, and how far"
 *
 * The child's OWN sweep vector comes from `thicknessSweepVector`, i.e. from
 * `drawing-model`'s `withThicknessAlongExtrusion` — the same call
 * `wcsBoundsLeaf.ts`, `cad-core/heavyPolylineDrawnBounds.ts` and
 * `polylineFromEntity.ts` all sweep with. It is then carried through
 * {@link transformBlockDirection}, which IS the insert's linear part (the same
 * function `./blockChildBandFrame.ts` builds its frame columns from). No third
 * normalization of group 210 and no second composition rule.
 *
 * # WHICH subtypes, and why only those
 *
 * The 2D subtype only, read through `polylineSubtype` — the gate
 * `../geom/polylineThicknessSweep.ts` already states from Autodesk's OCS
 * section: 3DPOLYLINE and MESH are listed among the entities that "do not lie
 * in a particular plane", and "of these entities, only lines and points can be
 * extruded". A 3D polyline and both mesh subtypes therefore have no thickness
 * surface at all, here or at top level. LWPOLYLINE / Hatch / Revision Cloud
 * children keep their own group 39 unrendered — the named round-15 follow-up
 * for the OTHER kinds' adapters, unchanged by this module, which deliberately
 * covers exactly the kind whose adapter paints one.
 */

import { polylineSubtype } from "@dxf-ai-editor/drawing-model";
import type { BlockChildEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";
import { thicknessSweepVector } from "../geom/polylineThicknessSweep";

import {
  transformBlockDirection,
  type BlockInsertTransform,
} from "./blockInsertTransform";

/**
 * The WORLD vector a block child's group-39 thickness displaces it by AFTER the
 * INSERT affine — `M·(t·N̂)` — or `null` when this child draws no swept surface
 * (every kind but the 2D heavy `POLYLINE`, and a thickness below
 * `withThicknessAlongExtrusion`'s own `1e-9` gate).
 *
 * `null` is the "nothing downstream changes" answer, exactly as
 * `blockChildBandFrame` uses it: {@link applyBlockChildThicknessSweep} returns
 * the renderable untouched and `transformBlockChild` leaves the clone's group 39
 * alone, so the overwhelmingly common thickness-less child stays byte-identical.
 */
export function blockChildThicknessSweep(
  child: BlockChildEntity,
  t: BlockInsertTransform,
): Vec3 | null {
  if (child.type !== "Polyline") return null;
  if (polylineSubtype(child) !== "2d") return null;
  const local = thicknessSweepVector(child.thickness, child.extrusion);
  if (local === null) return null;
  return transformBlockDirection(local, t);
}

/**
 * Stamp the transformed sweep onto a finished block-child renderable and widen
 * its AABB to the surface that sweep actually draws.
 *
 * The clone reaching `polylineFromEntity` carries NO group 39 at all when this
 * function has something to say (`transformBlockChild` zeroes it against the
 * very same {@link blockChildThicknessSweep} predicate, so the sweep can never
 * be resolved on the clone AND stamped here — the one-truth rule
 * `./blockChildBandFrame.ts` states for the width band). The base box therefore
 * arrives UNSWEPT and the union below is the whole answer rather than a second
 * helping on top of a scalar one.
 *
 * The union is EXACT rather than padded, for the reason
 * `polylineFromEntity.ts::sweptSceneBounds` and
 * `cad-core/heavyPolylineDrawnBounds.ts` both write for their own fold: the
 * sweep is a TRANSLATION, an AABB is translation-covariant, and every
 * coordinate of a ruled segment attains its extreme at one of its two ends, so
 * the swept set's box is the base box unioned with its own translate.
 */
export function applyBlockChildThicknessSweep(
  renderable: RenderableEntity,
  child: BlockChildEntity,
  t: BlockInsertTransform,
): RenderableEntity {
  const geometry = renderable.geometry;
  if (geometry.kind !== "polyline") return renderable;
  const sweep = blockChildThicknessSweep(child, t);
  if (!sweep) return renderable;
  const b = renderable.bounds;
  return {
    ...renderable,
    geometry: { ...geometry, thicknessSweep: sweep },
    bounds: {
      minX: Math.min(b.minX, b.minX + sweep.x),
      minY: Math.min(b.minY, b.minY + sweep.y),
      maxX: Math.max(b.maxX, b.maxX + sweep.x),
      maxY: Math.max(b.maxY, b.maxY + sweep.y),
    },
  };
}
